'use client';

/**
 * 글의 **브랜드별 부정** — 글 상세·소재 상세의 「부정 의견」 칸. 근거는 `readNegativeMentionGroups` 하나다.
 * 🔑 테두리 칸 하나 — 머리 「부정 의견」 + 「원문 / 번역」 고르개(대시보드 「부정 멘션」 칸과 같은 `NEGATIVE_TEXT_TABS` ·
 *   처음은 번역 · 번역이 하나도 없는 글이면 고르개 없이 머리 글만), 그 아래 브랜드마다 한 줄 — 왼쪽 브랜드 이름,
 *   오른쪽 문장들(문장마다 한 줄 · 둘째 문장부터는 첫 문장 아래로 맞춘다).
 * 🔑 빨강은 머리 「부정 의견」에만 쓴다 — 문장은 본문 색으로 읽힌다. 고르개는 작은 알약(`size="small"`).
 * ⚠️ 부정이 없으면 아무것도 안 그린다(자리도 안 잡는다). 카드·리스트·표에는 세우지 않는다.
 */

import type { Theme, SxProps } from '@mui/material/styles';

import { useState } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  NEGATIVE_TEXT_TABS,
  negativeGroupTexts,
  NEGATIVE_TEXT_DEFAULT,
  hasNegativeTranslation,
  readNegativeMentionGroups,
} from 'src/utils/moments/view/negative-evidence';

import { LINE, SPACE, RADIUS } from 'src/theme/ui-tokens';

import { SegmentTabs } from 'src/components/cmui/segment-tabs';

/**
 * @param post 어떤 모양의 글이든 받는다
 */
export function NegativeMentionList({
  post,
  sx,
}: {
  post: unknown;
  /** 바깥 여백만 — 부정이 없으면 이 여백도 안 생긴다 */
  sx?: SxProps<Theme>;
}) {
  const [mode, setMode] = useState(NEGATIVE_TEXT_DEFAULT);
  const groups = readNegativeMentionGroups(post);
  if (groups.length === 0) return null;
  const showTranslated = mode === 'translated';

  return (
    <Box
      sx={[
        {
          minWidth: 0,
          px: SPACE.dense,
          py: SPACE.row,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: RADIUS.sm,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={SPACE.row}
        sx={{ minWidth: 0 }}
      >
        <Typography variant="uiStrong" color="error.main" sx={{ flex: 1 }}>
          부정 의견
        </Typography>
        {hasNegativeTranslation(post) && (
          <SegmentTabs size="small" value={mode} onChange={setMode} tabs={NEGATIVE_TEXT_TABS} />
        )}
      </Stack>

      <Stack divider={<Box sx={{ borderTop: `1px solid ${LINE.hair}` }} />}>
        {groups.map((group) => (
          <Stack
            key={group.brand || '-'}
            direction="row"
            alignItems="baseline"
            spacing={SPACE.dense}
            sx={{ py: SPACE.tight, minWidth: 0 }}
          >
            {group.brand ? (
              <Typography
                variant="uiBody"
                color="text.secondary"
                sx={{ fontWeight: 600, flexShrink: 0, maxWidth: '30%', wordBreak: 'break-word' }}
              >
                {group.brand}
              </Typography>
            ) : null}
            <Stack spacing={SPACE.tight} sx={{ flex: 1, minWidth: 0 }}>
              {negativeGroupTexts(group, showTranslated).map((text, index) => (
                <Typography
                  key={index}
                  variant="uiBody"
                  color="text.primary"
                  sx={{ minWidth: 0, wordBreak: 'break-word' }}
                >
                  {text}
                </Typography>
              ))}
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
